<div class="appearance-container">
  <div class="title">Appearance</div>
  <div class="subtitle">Choose which 3D object or character you want to use.</div>
  
  <!-- Model Scale Settings - Moved to top for better accessibility -->
  <div class="model-scale-settings" *ngIf="selectedModel">
    <div class="settings-header">
      <span class="settings-title">Model Scale Settings</span>
      <div class="toggle-wrapper">
        <span>Auto Scale</span>
        <p-inputSwitch [(ngModel)]="autoScaleEnabled" 
                       (ngModelChange)="toggleAutoScale()">
        </p-inputSwitch>
      </div>
    </div>

    <div class="scale-inputs" [class.visible]="autoScaleEnabled">
      <div class="info-message">
        <i class="pi pi-info-circle"></i>
        <span>
          <ng-container *ngIf="isCustomModel(); else defaultModelMessage">
            When enabled, the model will be automatically scaled to ensure visibility while maintaining these proportions
          </ng-container>
          <ng-template #defaultModelMessage>
            Auto-scale is available for this model with default proportions
          </ng-template>
        </span>
      </div>

      <div class="input-row">
        <div class="input-group">
          <label>Width (meters)</label>
          <p-inputNumber [(ngModel)]="modelSize.x" 
                        [min]="0.1" 
                        (onInput)="updateModelSize()"
                        [showButtons]="true"
                        buttonLayout="horizontal"
                        spinnerMode="horizontal"
                        [step]="0.1"
                        [minFractionDigits]="1"
                        [maxFractionDigits]="2"
                        [style]="{'width': '150px'}"
                        [disabled]="!isCustomModel()">
          </p-inputNumber>
        </div>

        <div class="input-group">
          <label>Height (meters)</label>
          <p-inputNumber [(ngModel)]="modelSize.y"
                        [min]="0.1"
                        (onInput)="updateModelSize()"
                        [showButtons]="true"
                        buttonLayout="horizontal"
                        spinnerMode="horizontal"
                        [step]="0.1"
                        [minFractionDigits]="1"
                        [maxFractionDigits]="2"
                        [style]="{'width': '150px'}"
                        [disabled]="!isCustomModel()">
          </p-inputNumber>
        </div>

        <div class="input-group">
          <label>Depth (meters)</label>
          <p-inputNumber [(ngModel)]="modelSize.z"
                        [min]="0.1"
                        (onInput)="updateModelSize()"
                        [showButtons]="true"
                        buttonLayout="horizontal"
                        spinnerMode="horizontal"
                        [step]="0.1"
                        [minFractionDigits]="1"
                        [maxFractionDigits]="2"
                        [style]="{'width': '150px'}"
                        [disabled]="!isCustomModel()">
          </p-inputNumber>
        </div>
      </div>
    </div>
  </div>
  
  <div class="content-wrapper">
    <div class="model-display">
      <!-- <img *ngIf="!modelFile" [src]="selectedModel?.image" alt="Selected model" /> -->
      <app-threejs-viewer [modelUrl]="selectedModelUrl" class="model-viewer"></app-threejs-viewer>
    </div>

    <div class="options-panel">
      <div class="upload-section">
        <p-button 
          label="Upload your model" 
          icon="pi pi-upload" 
          (click)="showUploadModal()"
          styleClass="p-button-primary">
        </p-button>
        <div class="upload-info">Formats FBX, GLB, GLTF Limit of 50 mb</div>
      </div>

      <div class="assets-title">3D Models Library</div>
      <div class="assets-grid">
        <div *ngFor="let model of models" 
          (click)="selectModel(model)" 
          class="asset-item"
          [class.selected]="model.id === selectedModel?.id">
          <div class="model-preview">
            <ng-container *ngIf="model.isCustom; else defaultModel">
              <img [src]="model.image" [alt]="model.name" class="thumbnail">
              <button class="delete-button" 
                (click)="confirmDelete($event, model)"
                pTooltip="Delete model">
                <i class="pi pi-trash"></i>
              </button>
            </ng-container>
            <ng-template #defaultModel>
              <img [src]="getModelImagePath(model.id)" alt="3D asset" class="thumbnail">
              <button class="delete-button disabled" 
                pTooltip="Default models cannot be deleted">
                <i class="pi pi-trash"></i>
              </button>
            </ng-template>
          </div>
          <div class="model-name">{{model.isCustom ? model.name : 'Model ' + model.id}}</div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Upload Modal -->
<p-dialog 
  [(visible)]="uploadModalVisible" 
  [modal]="true" 
  [style]="{width: '600px'}"
  [draggable]="false" 
  [resizable]="false"
  [showHeader]="false"
  [contentStyle]="{'padding': '0'}"
  [baseZIndex]="1000">
  
  <div class="upload-modal-content">
    <!-- Header -->
    <div class="modal-header">
      <button class="close-button" (click)="uploadModalVisible = false">
        <i class="pi pi-times"></i>
      </button>
    </div>

    <!-- Steps -->
    <div class="steps-container">
      <div class="step" [class.active]="activeStep === 0">
        <div class="step-number">1</div>
        <div class="step-label">Upload Model</div>
      </div>
      <div class="step" [class.active]="activeStep === 1">
        <div class="step-number">2</div>
        <div class="step-label">Add Thumbnail</div>
      </div>
      <div class="step" [class.active]="activeStep === 2">
        <div class="step-number">3</div>
        <div class="step-label">Name Model</div>
      </div>
    </div>

    <!-- Content -->
    <div class="step-content">
      <ng-container [ngSwitch]="uploadStatus.state">
        <!-- Upload Steps -->
        <ng-container *ngSwitchCase="'idle'">
          <div [ngSwitch]="activeStep">
            <!-- Step 1: Model Upload -->
            <div *ngSwitchCase="0" class="upload-step">
              <p-fileUpload
                #modelUpload
                mode="basic"
                chooseLabel="Select 3D Model"
                [auto]="true"
                accept=".glb,.gltf,.fbx"
                [maxFileSize]="50000000"
                (onSelect)="onModelUpload($event)">
              </p-fileUpload>

              <div *ngIf="modelPreviewUrl" class="preview-container">
                <div class="preview-label">Preview:</div>
                <app-threejs-viewer [modelUrl]="modelPreviewUrl" class="model-preview"></app-threejs-viewer>
              </div>
            </div>

            <!-- Step 2: Thumbnail Upload -->
            <div *ngSwitchCase="1" class="upload-step">
              <p-fileUpload
                #thumbnailUpload
                mode="basic"
                chooseLabel="Select Thumbnail"
                [auto]="true"
                accept="image/*"
                [maxFileSize]="5000000"
                (onSelect)="onThumbnailUpload($event)">
              </p-fileUpload>

              <div *ngIf="thumbnailPreviewUrl" class="preview-container">
                <div class="preview-label">Preview:</div>
                <img [src]="thumbnailPreviewUrl" alt="Thumbnail preview" class="thumbnail-preview">
              </div>
            </div>

            <!-- Step 3: Model Name -->
            <div *ngSwitchCase="2" class="upload-step">
              <div class="name-input-container">
                <label for="modelName">Model Name</label>
                <input 
                  id="modelName"
                  type="text" 
                  pInputText 
                  [(ngModel)]="modelName" 
                  [disabled]="isUploading"
                  placeholder="Enter model name (letters, numbers, - and _ only)">
                <small class="hint">This name will be used to identify your model</small>
              </div>

              <div *ngIf="isUploading" class="upload-progress">
                <p-progressBar [value]="uploadProgress"></p-progressBar>
                <div class="progress-label">Uploading... {{uploadProgress}}%</div>
              </div>
            </div>
          </div>
        </ng-container>

        <!-- Upload Progress -->
        <div *ngSwitchCase="'uploading'" class="upload-status">
          <div class="status-icon">
            <i class="pi pi-spin pi-spinner"></i>
          </div>
          <h3>Uploading Model...</h3>
          <p-progressBar [value]="uploadProgress"></p-progressBar>
          <div class="progress-label">{{uploadProgress}}% Complete</div>
        </div>

        <!-- Success State -->
        <div *ngSwitchCase="'success'" class="upload-status">
          <div class="status-icon success">
            <i class="pi pi-check-circle"></i>
          </div>
          <h3>Upload Successful!</h3>
          <p>Your model has been uploaded and is now available in the library.</p>
        </div>

        <!-- Error State -->
        <div *ngSwitchCase="'error'" class="upload-status">
          <div class="status-icon error">
            <i class="pi pi-times-circle"></i>
          </div>
          <h3>Upload Failed</h3>
          <p>{{uploadStatus.message}}</p>
          <p class="error-details">{{uploadStatus.error?.message}}</p>
        </div>
      </ng-container>
    </div>

    <!-- Footer -->
    <div class="modal-footer">
      <ng-container [ngSwitch]="uploadStatus.state">
        <!-- Normal Steps Footer -->
        <ng-container *ngSwitchCase="'idle'">
          <p-button 
            label="Back" 
            icon="pi pi-chevron-left" 
            (click)="prevStep()" 
            [disabled]="activeStep === 0"
            styleClass="p-button-text">
          </p-button>
          <p-button 
            *ngIf="activeStep < 2"
            label="Next" 
            icon="pi pi-chevron-right" 
            iconPos="right"
            (click)="nextStep()"
            [disabled]="(activeStep === 0 && !uploadedModel) || (activeStep === 1 && !uploadedThumbnail)">
          </p-button>
          <p-button 
            *ngIf="activeStep === 2"
            label="Upload" 
            icon="pi pi-upload" 
            (click)="finishUpload()"
            [disabled]="!validateModelName()"
            styleClass="p-button-primary">
          </p-button>
        </ng-container>

        <!-- Success Footer -->
        <ng-container *ngSwitchCase="'success'">
          <p-button 
            label="Done" 
            icon="pi pi-check"
            (click)="closeUploadModal()"
            styleClass="p-button-success">
          </p-button>
        </ng-container>

        <!-- Error Footer -->
        <ng-container *ngSwitchCase="'error'">
          <p-button 
            label="Cancel" 
            icon="pi pi-times"
            (click)="closeUploadModal()"
            styleClass="p-button-text">
          </p-button>
          <p-button 
            label="Try Again" 
            icon="pi pi-refresh"
            (click)="resetUpload()"
            styleClass="p-button-danger">
          </p-button>
        </ng-container>
      </ng-container>
    </div>

    <!-- Add Toast component for messages -->
    <p-toast></p-toast>
  </div>
</p-dialog>

<!-- Add confirmation dialog -->
<app-confirmation-dialog
  [(visible)]="showDeleteConfirmation"
  [message]="deleteConfirmationMessage"
  (confirm)="onDeleteConfirm()"
  (cancel)="onDeleteCancel()">
</app-confirmation-dialog>